import React, { useState, useEffect, useMemo, useRef } from "react";
import styles from "./MapWrapper.module.css";
import buttonStyles from "./LocateMeButton.module.css";
import { Map, useMap } from "@vis.gl/react-google-maps";

import LoadingIndicator from "./LoadingIndicator";
import UserLocationDot from "./UserLocationDot";
import LanguageSwitcher from "./LanguageSwitcher";
import RecentReportsButton from "./RecentReportsButton";
import TrendsButton from "./TrendsButton";
import { LocationTrackerIcon } from "./Icon";
import RestoApiService from "../utils/RestoApiService";
import { sidebarStateManager, SidebarType } from "../utils/SidebarStateManager";
import { useSidebarState } from "../hooks/useSidebarState";
import { useTheme } from "../hooks/useTheme";

// Import new hooks
import { useMapState } from "../hooks/useMapState";
import { useRestaurantLoader } from "../hooks/useRestaurantLoader";
import { useMapDataFetcher } from "../hooks/useMapDataFetcher";
import { useMarkerManagement } from "../hooks/useMarkerManagement";
import { useUserLocation } from "../hooks/useUserLocation";
import { useMapEventHandlers } from "../hooks/useMapEventHandlers";
import { useCityBounds } from "../hooks/useCityBounds";

interface MapWrapperProps {
  selectedLocation: google.maps.LatLngLiteral | null;
  selectedRestaurantId: string | number | null;
  rangeValue: number[];
  setIsFiltering: React.Dispatch<React.SetStateAction<boolean>>;
  onRestaurantDeselect: () => void;
  onMapBoundsChange?: (bounds: google.maps.LatLngBounds | null) => void;
  onRestaurantSelect?: (data: { id: string | number; location: google.maps.LatLngLiteral }) => void;
}

export function MapWrapper({
  selectedLocation,
  selectedRestaurantId,
  rangeValue,
  setIsFiltering,
  onRestaurantDeselect,
  onMapBoundsChange,
  onRestaurantSelect
}: MapWrapperProps) {
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | null>(null);
  const map = useMap();
  const restoApiService = useMemo(() => new RestoApiService(), []);
  const initialCenterSet = useRef(false);
  const initialRestoIdProcessed = useRef<boolean>(false);
  
  // Use custom hooks
  const initialMapState = useMapState();
  const sidebarState = useSidebarState(sidebarStateManager);
  const { isDark } = useTheme();
  const { userLocation, handleLocateUser } = useUserLocation(map);
  
  const {
    isLoading,
    locations,
    setLocations,
    renderFromCache,
    debouncedFetchMapEntries,
  } = useMapDataFetcher({
    rangeValue,
    setIsFiltering,
    restoApiService,
    selectedMarkerId,
  });
  
  const { loadRestaurantById } = useRestaurantLoader({
    map,
    restoApiService,
    locations,
    selectedMarkerId,
    setLocations,
    setSelectedMarkerId,
  });
  
  const { isSelectingNewMarker } = useMarkerManagement({
    map,
    locations,
    selectedMarkerId,
    setSelectedMarkerId,
    onRestaurantSelect,
    onRestaurantDeselect,
  });
  
  useMapEventHandlers({
    map,
    mapBounds,
    setMapBounds,
    selectedMarkerId,
    initialCenterSet,
    onMapBoundsChange,
    onRestaurantDeselect,
    debouncedFetchMapEntries,
    initialMapCenter: initialMapState.center,
  });

  // Use city bounds hook to handle initial city parameter
  useCityBounds({ map, restoApiService });


  // Effect to handle restaurant selection from URL parameter on mount
  useEffect(() => {
    // Only process if map is ready and we haven't processed yet
    if (!map || initialRestoIdProcessed.current) {
      return;
    }
    
    // Mark as processed immediately to prevent re-runs
    initialRestoIdProcessed.current = true;
    
    // If we have a selectedRestaurantId on mount (from URL parameter)
    if (selectedRestaurantId) {
      const restoId = String(selectedRestaurantId);
      
      // Create an async function to handle the loading and awaiting
      const loadAndZoomToRestaurant = async () => {
        try {
          // Load the restaurant and zoom to it - await the async operation
          await loadRestaurantById(restoId, true);
          
          // Ensure sidebar opens after loading
          sidebarStateManager.openSidebar(SidebarType.RESTAURANT_DETAILS);
        } catch (error) {
        }
      };
      
      // Execute the async function
      loadAndZoomToRestaurant();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]); // Only depend on map availability, not on changing functions or IDs

  // Effect to handle restaurant details sidebar closing
  useEffect(() => {
    const unsubscribe = sidebarStateManager.subscribe((state) => {
      
      // Handle cleanup when restaurant details sidebar is closed
      if (state.previousSidebar === SidebarType.RESTAURANT_DETAILS && state.activeSidebar === null) {
        // Clear the selected marker ID
        setSelectedMarkerId(null);

        // Clear the URL parameter
        history.replaceState({}, "", window.location.pathname);
        
        // Call the parent deselection callback
        onRestaurantDeselect();
      }

      // Add log to check if RECENT_REPORTS sidebar closure is detected
      if (state.previousSidebar === SidebarType.RECENT_REPORTS && state.activeSidebar !== SidebarType.RECENT_REPORTS) {
        // Add deselection logic for RECENT_REPORTS here if needed
      }
      
      // Handle TRENDS sidebar closure
      if (state.previousSidebar === SidebarType.TRENDS && state.activeSidebar !== SidebarType.TRENDS) {
        // Add deselection logic for TRENDS here if needed
      }
    });

    return unsubscribe;
  }, [onRestaurantDeselect]);


  // Effect to handle selection coming from search component or URL
  useEffect(() => {
    // If restaurant is deselected at app level, clear local state
    if (selectedRestaurantId === null) {
      setSelectedMarkerId(null);
      return;
    }
    
    const restoId = String(selectedRestaurantId);
    
    // If the same restaurant is already selected locally, just ensure sidebar is open
    if (selectedMarkerId === restoId) {
      sidebarStateManager.openSidebar(SidebarType.RESTAURANT_DETAILS);
      return;
    }
    
    // Skip if we're currently selecting from marker click
    // This prevents the effect from interfering with marker selection
    if (isSelectingNewMarker.current) {
      return;
    }
    
    // Use a single timeout to debounce multiple rapid selections
    const timer = setTimeout(async () => {
      try {
        // Check if this restaurant is already in our locations array
        const locationExists = locations.some((loc) => loc.id === restoId);

        if (locationExists) {
          // If it exists, set the selectedMarkerId to open the sidebar
          setSelectedMarkerId(restoId);
          
          // Find the location to center and zoom the map
          const location = locations.find(loc => loc.id === restoId);
          if (location && map) {
            const position = { lat: location.lat, lng: location.lng };
            // Only pan to the location, don't zoom
            // This prevents interfering with marker click behavior
            map.panTo(position);
          }
        } else {
          // Use the loadRestaurantById function to ensure consistent behavior
          // Only zoom if this is from search/URL, not from marker click
          const shouldZoom = !isSelectingNewMarker.current;
          await loadRestaurantById(restoId, shouldZoom);
        }
      } catch (error) {
        console.error("Error processing restaurant selection:", error);
      }
    }, 100); // 100ms debounce to prevent rapid state updates

    return () => clearTimeout(timer);
  }, [selectedRestaurantId, locations, restoApiService, loadRestaurantById, map]); // Removed selectedMarkerId to prevent circular dependency


  
  // Effect to trigger fetching when bounds or range changes
  useEffect(() => {
    if (mapBounds) {
      // IMMEDIATE: Render cached data instantly (no delay)
      renderFromCache(mapBounds);

      // DEBOUNCED: Fetch fresh data from API (after 300ms)
      debouncedFetchMapEntries(mapBounds);
    }
  }, [mapBounds, rangeValue, renderFromCache, debouncedFetchMapEntries]);

  useEffect(() => {
    return () => {
      debouncedFetchMapEntries.cancel();
    };
  }, [debouncedFetchMapEntries]);

  // --- Set initial map center/zoom and handle selectedLocation changes ---
  useEffect(() => {
    if (!map) return;

    if (selectedLocation) {
      // Only pan to the location without changing zoom
      map.panTo(selectedLocation);
      initialCenterSet.current = true;
    } else if (!initialCenterSet.current) {
      map.setCenter(initialMapState.center);
      map.setZoom(initialMapState.zoom);
      initialCenterSet.current = true;
    }
  }, [selectedLocation, map, initialMapState]);






  return (
    <div className={styles["map-wrapper"]}>
      {/* Map component: defaultCenter/Zoom removed as we set it programmatically */}
      <Map
        mapId="3dd269ba7ee95609"
        colorScheme={isDark ? "DARK" : "LIGHT"}
        defaultZoom={initialMapState.zoom}
        onClick={() => {
          // Check if any sidebar is active and close it
          if (sidebarStateManager.hasActiveSidebar()) {
            sidebarStateManager.closeSidebar();
          }
          
          // Keep existing restaurant-specific logic for backward compatibility
          if (selectedMarkerId) {
            setSelectedMarkerId(null);
            onRestaurantDeselect();
          }
        }}
        gestureHandling="greedy"
        defaultCenter={initialMapState.center}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        disableDefaultUI={true}
        clickableIcons={false} // Disable POI click events
        // Disable all default POI clicks
        styles={[
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }]
          }
        ]}
      >
        {/* Markers are managed by MarkerClusterManager */}

        {/* Show User's Location */}
        {userLocation && <UserLocationDot position={userLocation} />}
      </Map>
      <button
        className={`${buttonStyles["locate-me-button"]} ${sidebarState.hasActiveSidebar() ? buttonStyles["sidebar-open"] : ""}`}
        onClick={handleLocateUser}
        aria-label="Locate me"
      >
        <LocationTrackerIcon className={buttonStyles["location-tracker-icon"]} />
      </button>
      
      {/* Language Switcher - positioned in top-right corner */}
      <LanguageSwitcher
        variant="floating"
        showFlags={true}
        showText={true}
        isSidebarOpen={sidebarState.hasActiveSidebar()}
      />
      
      {/* Recent Reports Button - positioned above LocateMeButton */}
      <RecentReportsButton
        onClick={() => sidebarState.toggleSidebar(SidebarType.RECENT_REPORTS)}
        isSidebarOpen={sidebarState.hasActiveSidebar()}
        isActive={sidebarState.isSidebarActive(SidebarType.RECENT_REPORTS)}
      />

      {/* Trends Button - positioned above RecentReportsButton */}
      <TrendsButton
        onClick={() => sidebarState.toggleSidebar(SidebarType.TRENDS)}
        isSidebarOpen={sidebarState.hasActiveSidebar()}
        isActive={sidebarState.isSidebarActive(SidebarType.TRENDS)}
      />

      {/* Show location permission overlay when needed */}
      {/* Show restaurant details sidebar when a marker is selected */}
      <LoadingIndicator isLoading={isLoading} />
      
      {/* Toast notification removed as it's not being used */}
    </div>
  );
}
